import React, { useEffect, useState } from 'react'

import { KeyboardArrowDown } from '@mui/icons-material'
import { Button, Menu, MenuItem } from '@mui/material'
import { useLocation } from 'react-router-dom'

import router from '../../../router/functions/router.functions'
import { UserRole } from '../../functions/UserRole/userRoleManagement.types'
import { getRoleFromPath, getUserRole, setUserRole } from '../../functions/UserRole/userRoleManagment.functions'
import styles from './RoleSwitcher.styles'
import { RoleOption, RoleSwitcherProps } from './RoleSwitcher.types'

// Using hardcoded paths to avoid circular dependency with routes.dictionary
const roleOptions: RoleOption[] = [
  { value: UserRole.shopper, label: 'Shopping', route: 'shop' },
  { value: UserRole.traveler, label: 'Traveling', route: '/' },
  { value: UserRole.sender, label: 'Sending', route: 'send' }
]

const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ currentRole }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const navigate = router.navigate()
  const location = useLocation()
  const currentPage = router.getCurrentPage()
  const open = Boolean(anchorEl)

  // Determine role from current route or props
  // Priority: currentRole prop > browser pathname > currentPage.path > stored role > default
  const determineRole = React.useMemo((): UserRole => {
    if (currentRole) {
      return currentRole
    }

    const roleFromBrowserPath = getRoleFromPath(location.pathname)
    if (roleFromBrowserPath) {
      return roleFromBrowserPath
    }

    const roleFromPagePath = getRoleFromPath(currentPage.path)
    if (roleFromPagePath) {
      return roleFromPagePath
    }

    const storedRole = getUserRole()

    return storedRole || UserRole.shopper
  }, [currentRole, location.pathname, currentPage.path])

  const [selectedRole, setSelectedRole] = useState<UserRole>(determineRole)

  useEffect(() => {
    setSelectedRole(determineRole)
  }, [determineRole])

  const currentRoleLabel = roleOptions.find((option) => option.value === selectedRole)?.label || 'Shopping'

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRoleChange = (newRole: UserRole) => {
    setUserRole(newRole)
    setSelectedRole(newRole)
    const roleOption = roleOptions.find((option) => option.value === newRole)

    if (roleOption) {
      navigate(roleOption.route)
    }

    handleClose()
  }

  return (
    <>
      <Button
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        sx={styles.button}
        aria-controls={open ? 'role-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {currentRoleLabel}
      </Button>
      <Menu
        id="role-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'role-button'
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={styles.menu}
      >
        {roleOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleRoleChange(option.value)}
            selected={option.value === selectedRole}
            sx={styles.menuItem}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default RoleSwitcher
